import { auth } from './firebase.js';

const BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080';

async function authHeaders() {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

async function request(path, { method = 'GET', body, auth: needAuth = false } = {}) {
  const headers = { 'Content-Type': 'application/json' };
  if (needAuth) Object.assign(headers, await authHeaders());
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) {
    const err = new Error(data.error || `HTTP ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

// Public
export const Public = {
  products: () => request('/public/products'),
  events: (start, end) =>
    request(
      `/public/events${start || end ? `?${new URLSearchParams({ ...(start && { start }), ...(end && { end }) })}` : ''}`,
    ),
  teams: () => request('/public/teams'),
  signupFields: () => request('/public/signup-fields'),
  joinRequest: (body) => request('/public/join-request', { method: 'POST', body }),
  clubhouseInquiry: (body) => request('/public/clubhouse-inquiry', { method: 'POST', body }),
  unsubscribe: (token) => request(`/public/unsubscribe?token=${encodeURIComponent(token)}`),
};

// Member
export const Me = {
  get: () => request('/me/', { auth: true }),
  update: (body) => request('/me/', { method: 'PATCH', body, auth: true }),
  bootstrap: (body) => request('/me/bootstrap', { method: 'POST', body, auth: true }),
  orders: () => request('/me/orders', { auth: true }),
};

export const Checkout = {
  create: (items) => request('/checkout/session', { method: 'POST', body: { items }, auth: true }),
};

// Admin
const a = (path, opts = {}) => request(`/admin${path}`, { ...opts, auth: true });
export const Admin = {
  users: {
    list: () => a('/users/'),
    grantAdmin: (uid) => a('/users/grant-admin', { method: 'POST', body: { uid } }),
    grantSuperadmin: (uid) => a('/users/grant-superadmin', { method: 'POST', body: { uid } }),
    revokeAdmin: (uid) => a('/users/revoke-admin', { method: 'POST', body: { uid } }),
    setPermissions: (uid, permissions) =>
      a(`/users/${uid}/permissions`, { method: 'PATCH', body: { uid, permissions } }),
    getDefaultPermissions: () => a('/users/default-permissions'),
    setDefaultPermissions: (permissions) =>
      a('/users/default-permissions', { method: 'PATCH', body: permissions }),
    remove: (uid) => a(`/users/${uid}`, { method: 'DELETE' }),
  },
  teams: {
    list: () => a('/teams/'),
    create: (body) => a('/teams/', { method: 'POST', body }),
    update: (id, body) => a(`/teams/${id}`, { method: 'PATCH', body }),
    remove: (id) => a(`/teams/${id}`, { method: 'DELETE' }),
  },
  players: {
    list: (teamId) => a(`/players/${teamId ? `?teamId=${teamId}` : ''}`),
    get: (id) => a(`/players/${id}`),
    create: (body) => a('/players/', { method: 'POST', body }),
    update: (id, body) => a(`/players/${id}`, { method: 'PATCH', body }),
    remove: (id) => a(`/players/${id}`, { method: 'DELETE' }),
  },
  events: {
    list: (start, end) =>
      a(
        `/events/${start || end ? `?${new URLSearchParams({ ...(start && { start }), ...(end && { end }) })}` : ''}`,
      ),
    create: (body) => a('/events/', { method: 'POST', body }),
    update: (id, body) => a(`/events/${id}`, { method: 'PATCH', body }),
    remove: (id) => a(`/events/${id}`, { method: 'DELETE' }),
  },
  bookings: {
    list: (status) => a(`/bookings/${status ? `?status=${status}` : ''}`),
    create: (body) => a('/bookings/', { method: 'POST', body }),
    paymentLink: (id, body) => a(`/bookings/${id}/payment-link`, { method: 'POST', body }),
    approve: (id, body) => a(`/bookings/${id}/approve`, { method: 'POST', body }),
    reject: (id) => a(`/bookings/${id}/reject`, { method: 'POST' }),
    cancel: (id) => a(`/bookings/${id}/cancel`, { method: 'POST' }),
  },
  products: {
    list: () => a('/products/'),
    create: (body) => a('/products/', { method: 'POST', body }),
    update: (id, body) => a(`/products/${id}`, { method: 'PATCH', body }),
    remove: (id) => a(`/products/${id}`, { method: 'DELETE' }),
  },
  campaigns: {
    list: () => a('/campaigns/'),
    get: (id) => a(`/campaigns/${id}`),
    create: (body) => a('/campaigns/', { method: 'POST', body }),
    send: (id) => a(`/campaigns/${id}/send`, { method: 'POST' }),
    sendTest: (id) => a(`/campaigns/${id}/send-test`, { method: 'POST' }),
    remove: (id) => a(`/campaigns/${id}`, { method: 'DELETE' }),
  },
  joinRequests: {
    list: (status) => a(`/join-requests/${status ? `?status=${status}` : ''}`),
    accept: (id) => a(`/join-requests/${id}/accept`, { method: 'POST' }),
    reject: (id) => a(`/join-requests/${id}/reject`, { method: 'POST' }),
    update: (id, body) => a(`/join-requests/${id}`, { method: 'PATCH', body }),
  },
  contactMessages: {
    list: (status) => a(`/contact-messages/${status ? `?status=${status}` : ''}`),
    get: (id) => a(`/contact-messages/${id}`),
    update: (id, body) => a(`/contact-messages/${id}`, { method: 'PATCH', body }),
    markReplied: (id) => a(`/contact-messages/${id}/mark-replied`, { method: 'POST' }),
    archive: (id) => a(`/contact-messages/${id}/archive`, { method: 'POST' }),
    remove: (id) => a(`/contact-messages/${id}`, { method: 'DELETE' }),
  },
  settings: {
    stripe: {
      get: () => a('/settings/stripe'),
      save: (body) => a('/settings/stripe', { method: 'PATCH', body }),
      clear: () => a('/settings/stripe', { method: 'DELETE' }),
      test: (secretKey) => a('/settings/stripe/test', { method: 'POST', body: secretKey ? { secretKey } : {} }),
      autoSetupWebhook: (secretKey) =>
        a('/settings/stripe/webhook/auto-setup', { method: 'POST', body: secretKey ? { secretKey } : {} }),
    },
    email: {
      get: () => a('/settings/email'),
      save: (body) => a('/settings/email', { method: 'PATCH', body }),
      clear: () => a('/settings/email', { method: 'DELETE' }),
      test: (to) => a('/settings/email/test', { method: 'POST', body: to ? { to } : {} }),
    },
    rental: {
      get: () => a('/settings/rental'),
      save: (body) => a('/settings/rental', { method: 'PATCH', body }),
      clear: () => a('/settings/rental', { method: 'DELETE' }),
    },
  },
  contacts: {
    list: (filters = {}) => {
      const qs = new URLSearchParams(Object.entries(filters).filter(([, v]) => v)).toString();
      return a(`/contacts/${qs ? `?${qs}` : ''}`);
    },
    create: (body) => a('/contacts/', { method: 'POST', body }),
    update: (id, body) => a(`/contacts/${id}`, { method: 'PATCH', body }),
    remove: (id) => a(`/contacts/${id}`, { method: 'DELETE' }),
    sync: () => a('/contacts/sync', { method: 'POST' }),
  },
  signupFields: {
    list: () => a('/signup-fields/'),
    create: (body) => a('/signup-fields/', { method: 'POST', body }),
    update: (id, body) => a(`/signup-fields/${id}`, { method: 'PATCH', body }),
    remove: (id) => a(`/signup-fields/${id}`, { method: 'DELETE' }),
    reorder: (ids) => a('/signup-fields/reorder', { method: 'POST', body: { ids } }),
  },
  newsPosts: {
    list: (status) => a(`/news-posts/${status ? `?status=${status}` : ''}`),
    get: (id) => a(`/news-posts/${id}`),
    create: (body) => a('/news-posts/', { method: 'POST', body }),
    update: (id, body) => a(`/news-posts/${id}`, { method: 'PATCH', body }),
    remove: (id) => a(`/news-posts/${id}`, { method: 'DELETE' }),
  },
  meetings: {
    list: () => a('/meetings/'),
    get: (id) => a(`/meetings/${id}`),
    create: (body) => a('/meetings/', { method: 'POST', body }),
    update: (id, body) => a(`/meetings/${id}`, { method: 'PATCH', body }),
    remove: (id) => a(`/meetings/${id}`, { method: 'DELETE' }),
  },
  lists: {
    list: () => a('/lists/'),
    get: (id) => a(`/lists/${id}`),
    create: (body) => a('/lists/', { method: 'POST', body }),
    update: (id, body) => a(`/lists/${id}`, { method: 'PATCH', body }),
    remove: (id) => a(`/lists/${id}`, { method: 'DELETE' }),
    members: (id) => a(`/lists/${id}/contacts`),
    addMembers: (id, contactIds) => a(`/lists/${id}/contacts`, { method: 'POST', body: { contactIds } }),
    removeMember: (id, contactId) => a(`/lists/${id}/contacts/${contactId}`, { method: 'DELETE' }),
  },
};
